import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Code2, Smartphone, Globe2, Database, Zap, ShieldCheck,
  ArrowUpRight, Layers, GitBranch, Cpu
} from "lucide-react";
import PageHero from "../components/PageHero";

const STACK = [
  { tier: "Frontend", items: ["React 19", "Next.js 15", "TypeScript", "Tailwind", "Framer Motion"], icon: Layers },
  { tier: "Backend", items: ["Node.js", "FastAPI", "Go", "PostgreSQL", "Redis", "RabbitMQ"], icon: Cpu },
  { tier: "Infra", items: ["AWS", "GCP", "Cloudflare", "Equinix LD4 / NY4", "Kubernetes"], icon: Globe2 },
  { tier: "Mobile", items: ["React Native", "Swift", "Kotlin", "Expo EAS"], icon: Smartphone },
  { tier: "Data", items: ["Snowflake", "BigQuery", "ClickHouse", "dbt", "Airflow"], icon: Database },
  { tier: "DevEx", items: ["GitHub Actions", "Linear", "Sentry", "Datadog", "PostHog"], icon: GitBranch },
];

const CASES = [
  { title: "Marketing rebuild", desc: "Multi-region landing site with localization for 12 languages. Lighthouse score 98+." },
  { title: "Trader's Room v2", desc: "Full CRM trader dashboard rewritten in React 19 — 4× faster load, 60% fewer support tickets." },
  { title: "Mobile companion app", desc: "iOS + Android apps with biometric login and FaceID-secured trading approvals." },
];

export default function WebTechPage() {
  return (
    <>
      <PageHero
        eyebrow="Web-Tech"
        breadcrumb={[{ label: "Web-Tech" }]}
        title={<>Frontend & infra <span className="text-gradient-cyan italic">that scales</span> with your desk.</>}
        lede="A production-minded web stack with clear standards where reliability matters and flexibility where products evolve. The same engineers behind our broker platform deliver your customer-facing surfaces."
      />

      {/* Stack grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-10">
            <span className="w-10 h-px bg-[#00C2FF]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#00C2FF] font-bold">
              / Our stack
            </span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {STACK.map((s, i) => (
              <motion.div
                key={s.tier}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="p-6 rounded-2xl theme-panel border hover:border-[#00C2FF]/40 transition-colors"
                data-testid={`webtech-stack-${i}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00C2FF]/10 border border-[#00C2FF]/30 text-[#00C2FF] flex items-center justify-center">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display text-lg font-semibold">{s.tier}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {s.items.map((item) => (
                    <span
                      key={item}
                      className="font-mono text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-gray-300"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Performance promise */}
      <section className="py-20 border-y border-white/10 theme-section-alt">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          {[
            { Icon: Zap, val: "<1.5s", l: "P75 page load" },
            { Icon: ShieldCheck, val: "AAA", l: "Accessibility target" },
            { Icon: Code2, val: "100%", l: "Source code ownership" },
          ].map((s, i) => (
            <motion.div
              key={s.l}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <s.Icon className="w-7 h-7 text-[#00C2FF] mx-auto mb-3" />
              <div className="font-display text-4xl sm:text-5xl font-extrabold text-gradient-cyan">{s.val}</div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-gray-500 mt-3">{s.l}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Sample cases */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mb-10">
            Sample <span className="text-gradient-cyan italic">recent work.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {CASES.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="p-6 rounded-2xl theme-panel border hover:border-[#00C2FF]/50 transition-all hover:-translate-y-1"
                data-testid={`webtech-case-${i}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00C2FF] mb-3">
                  Case {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display text-xl font-semibold mb-2">{c.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-white/10 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Got a build in mind?
          </h2>
          <Link
            to="/contact"
            className="btn-cyan inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-full font-semibold"
          >
            Start a project <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
